/**
 * Portfolio Page
 *
 * User's own portfolio editing page. Allows editing profile info,
 * managing featured clips, and customizing appearance.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetMyPortfolioQuery,
  useUpdateMyPortfolioMutation,
  useGetMyHighlightsQuery,
  useUpsertHighlightMutation,
  useDeleteHighlightMutation,
  useSyncMyPortfolioMutation,
  useGetMyPublicProfileQuery,
  usePublishMyProfileMutation,
  useUnpublishMyProfileMutation,
  PortfolioTheme,
  type PortfolioHighlight,
} from '../store/api/portfolioApi';
import { ShareProfileModal } from '../components/profile/ShareProfileModal';
import { MarketplacePublishSection } from '../components/marketplace/MarketplacePublishSection';
import { ResultProvenance } from '../components/portfolio/ResultProvenance';
import toast from 'react-hot-toast';
import { mutationOutcome } from '../utils/mutationOutcome';

/** The Basic Information form's fields. */
export interface PortfolioBasicsForm {
  displayName: string;
  headline: string;
  bio: string;
  slug: string;
  location: string;
}

/** The form, filled from the saved portfolio (never blank over saved values). */
export function portfolioBasicsForm(
  portfolio: { displayName?: string | null; headline?: string | null; bio?: string | null; slug?: string | null; location?: string | null } | null | undefined
): PortfolioBasicsForm {
  return {
    displayName: portfolio?.displayName || '',
    headline: portfolio?.headline || '',
    bio: portfolio?.bio || '',
    slug: portfolio?.slug || '',
    location: portfolio?.location || '',
  };
}

/**
 * PUT /portfolio/me body for the Basic Information form: only the fields that
 * form edits. It used to send the whole page state — isPublic: false,
 * availableForHire: false, rate 0 — so saving a name unpublished the profile
 * and cleared hire settings. An empty slug is omitted (the server requires 3+
 * characters; blank means "unchanged", not a 422).
 */
export function portfolioBasicsPayload(form: PortfolioBasicsForm) {
  return {
    displayName: form.displayName,
    headline: form.headline,
    bio: form.bio,
    location: form.location,
    ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
  };
}

export const SETTING_SAVE_FAILED = 'That setting could not be saved. Please try again.';

export function PortfolioPage() {
  const { data: portfolio, isLoading, error } = useGetMyPortfolioQuery();
  const { data: highlights } = useGetMyHighlightsQuery();
  const [updatePortfolio, { isLoading: isUpdating }] = useUpdateMyPortfolioMutation();
  const [syncPortfolio, { isLoading: isSyncing }] = useSyncMyPortfolioMutation();
  const { data: publicProfileData } = useGetMyPublicProfileQuery();
  const [publishProfile, { isLoading: isPublishing }] = usePublishMyProfileMutation();
  const [unpublishProfile, { isLoading: isUnpublishing }] = useUnpublishMyProfileMutation();

  const [editMode, setEditMode] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [formData, setFormData] = useState<PortfolioBasicsForm>(() => portfolioBasicsForm(portfolio));

  // Edit opens the form on the SAVED values. (A one-shot useState initializer
  // used to run before the portfolio loaded, so the form opened blank.)
  const toggleEdit = () => {
    if (!editMode) setFormData(portfolioBasicsForm(portfolio));
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    try {
      await updatePortfolio(portfolioBasicsPayload(formData)).unwrap();
      setEditMode(false);
      toast.success('Profile saved');
    } catch (err) {
      console.error('Failed to update portfolio:', err);
      const message = (err as { data?: { message?: unknown } })?.data?.message;
      toast.error(typeof message === 'string' && message ? message : 'Your changes could not be saved. Please try again.');
    }
  };

  // Settings toggles are controlled by the saved portfolio, so a failed save
  // leaves them where the server has them — and says so instead of snapping
  // back silently.
  const saveSetting = (call: { unwrap: () => Promise<unknown> }) =>
    mutationOutcome(call, SETTING_SAVE_FAILED, toast.error);

  const handleSync = async () => {
    try {
      await syncPortfolio().unwrap();
    } catch (err) {
      console.error('Failed to sync portfolio:', err);
      toast.error('Your portfolio could not be synced. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load portfolio</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Portfolio</h1>
          <p className="mt-1 text-gray-500">
            Customize your public profile and showcase your skills
          </p>
        </div>
        <div className="flex gap-3">
          {publicProfileData?.profile?.isPublic && publicProfileData?.profile?.publicSlug && (
            <>
              <button
                onClick={() => setShareModalOpen(true)}
                className="btn btn-primary"
              >
                <ShareIcon className="w-4 h-4 mr-2" />
                Share Profile
              </button>
              <Link
                to={`/u/${publicProfileData.profile.publicSlug}`}
                className="btn btn-outline"
                target="_blank"
              >
                <EyeIcon className="w-4 h-4 mr-2" />
                View Public Profile
              </Link>
            </>
          )}
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="btn btn-outline"
          >
            {isSyncing ? (
              <span className="animate-spin">...</span>
            ) : (
              <RefreshIcon className="w-4 h-4 mr-2" />
            )}
            Sync from Activity
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Basic Information</h2>
              <button
                onClick={toggleEdit}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            </div>
            <div className="card-body space-y-4">
              {editMode ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      className="input w-full"
                      placeholder="Your display name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Headline
                    </label>
                    <input
                      type="text"
                      value={formData.headline}
                      onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                      className="input w-full"
                      placeholder="e.g., Sales Professional | Public Speaker"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bio
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="input w-full h-32"
                      placeholder="Tell others about yourself..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom URL Slug
                    </label>
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-1">{window.location.host}/p/</span>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        className="input flex-1"
                        placeholder="your-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="input w-full"
                      placeholder="City, Country"
                    />
                  </div>
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSave}
                      disabled={isUpdating}
                      className="btn btn-primary"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary-600">
                        {(portfolio?.displayName || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {portfolio?.displayName || 'No name set'}
                      </h3>
                      <p className="text-gray-500">{portfolio?.headline || 'No headline set'}</p>
                    </div>
                  </div>
                  {portfolio?.bio && (
                    <p className="text-gray-600">{portfolio.bio}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    {portfolio?.location && (
                      <span className="flex items-center gap-1">
                        <LocationIcon className="w-4 h-4" />
                        {portfolio.location}
                      </span>
                    )}
                    {portfolio?.slug && (
                      <span className="flex items-center gap-1">
                        <LinkIcon className="w-4 h-4" />
                        {window.location.host}/p/{portfolio.slug}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sample Calls Card */}
          <SampleCallsManager highlights={highlights ?? []} />
        </div>

        {/* Right column - Settings */}
        <div className="space-y-6">
          {/* Public Profile Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Public Profile</h2>
            </div>
            <div className="card-body space-y-4">
              {/* Eligibility & Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <p className="text-xs text-gray-500">
                    {publicProfileData?.canPublish
                      ? 'Ready to be discovered'
                      : 'Not yet eligible — keep practicing and pass a skill practice run'}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    publicProfileData?.profile?.isPublic
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {publicProfileData?.profile?.isPublic ? 'Published' : 'Unpublished'}
                </span>
              </div>

              {/* Publish/Unpublish Button */}
              {publicProfileData?.canPublish && (
                <div>
                  {publicProfileData?.profile?.isPublic ? (
                    <button
                      onClick={() => void saveSetting(unpublishProfile())}
                      disabled={isUnpublishing}
                      className="btn btn-outline w-full text-red-600 border-red-300 hover:bg-red-50"
                    >
                      {isUnpublishing ? 'Unpublishing...' : 'Unpublish Profile'}
                    </button>
                  ) : (
                    <button
                      onClick={() => void saveSetting(publishProfile())}
                      disabled={isPublishing}
                      className="btn btn-primary w-full"
                    >
                      {isPublishing ? 'Publishing...' : 'Publish Profile'}
                    </button>
                  )}
                </div>
              )}

              {/* Shareable Link */}
              {publicProfileData?.profile?.isPublic && publicProfileData?.profile?.publicSlug && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 mb-1">Shareable Link</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-primary-600 flex-1 truncate">
                      {window.location.origin}/u/{publicProfileData.profile.publicSlug}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/u/${publicProfileData.profile?.publicSlug}`
                        );
                      }}
                      className="text-gray-500 hover:text-gray-700"
                      title="Copy link"
                    >
                      <CopyIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* View Stats */}
              {publicProfileData?.viewStats && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">
                      {publicProfileData.viewStats.totalViews}
                    </p>
                    <p className="text-xs text-gray-500">Total Views</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">
                      {publicProfileData.viewStats.uniqueViewers}
                    </p>
                    <p className="text-xs text-gray-500">Unique</p>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">
                      {publicProfileData.viewStats.recentViews}
                    </p>
                    <p className="text-xs text-gray-500">7 Days</p>
                  </div>
                </div>
              )}

              {/* Public Profile Visibility Settings */}
              {publicProfileData?.profile && (
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">What to Show</p>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Authority Scores</span>
                    <input
                      type="checkbox"
                      checked={publicProfileData.profile.portfolioSettings?.showAuthorityScores ?? true}
                      onChange={async (e) => {
                        await saveSetting(publishProfile({ showAuthorityScores: e.target.checked }));
                      }}
                      className="toggle"
                      disabled={!publicProfileData.profile.isPublic}
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Badges</span>
                    <input
                      type="checkbox"
                      checked={publicProfileData.profile.portfolioSettings?.showBadges ?? true}
                      onChange={async (e) => {
                        await saveSetting(publishProfile({ showBadges: e.target.checked }));
                      }}
                      className="toggle"
                      disabled={!publicProfileData.profile.isPublic}
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Sample Calls</span>
                    <input
                      type="checkbox"
                      checked={publicProfileData.profile.portfolioSettings?.showFeaturedClips ?? true}
                      onChange={async (e) => {
                        await saveSetting(publishProfile({ showFeaturedClips: e.target.checked }));
                      }}
                      className="toggle"
                      disabled={!publicProfileData.profile.isPublic}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Marketplace Publishing */}
          <MarketplacePublishSection />

          {/* Visibility Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Visibility</h2>
            </div>
            <div className="card-body space-y-4">
              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Public Profile</span>
                <input
                  type="checkbox"
                  checked={portfolio?.isPublic || false}
                  onChange={async (e) => {
                    await saveSetting(updatePortfolio({ isPublic: e.target.checked }));
                  }}
                  className="toggle"
                />
              </label>
              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Available for Hire</span>
                <input
                  type="checkbox"
                  checked={portfolio?.availableForHire || false}
                  onChange={async (e) => {
                    await saveSetting(updatePortfolio({ availableForHire: e.target.checked }));
                  }}
                  className="toggle"
                />
              </label>
            </div>
          </div>

          {/* Rate Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Rate</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate Type
                </label>
                <select
                  value={portfolio?.rateType || 'none'}
                  onChange={async (e) => {
                    await saveSetting(updatePortfolio({ rateType: e.target.value as any }));
                  }}
                  className="input w-full"
                >
                  <option value="none">Not specified</option>
                  <option value="hour">Per Hour</option>
                  <option value="session">Per Session</option>
                  <option value="project">Per Project</option>
                </select>
              </div>
              {portfolio?.rateType !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount ({portfolio?.currency || 'USD'})
                  </label>
                  <input
                    type="number"
                    value={portfolio?.rateAmount || 0}
                    onChange={async (e) => {
                      await saveSetting(updatePortfolio({ rateAmount: parseInt(e.target.value) }));
                    }}
                    className="input w-full"
                    min="0"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Theme Card */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Theme</h2>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-2 gap-2">
                {(['default', 'minimal', 'bold', 'professional'] as PortfolioTheme[]).map((theme) => (
                  <button
                    key={theme}
                    onClick={async () => {
                      await saveSetting(updatePortfolio({ theme }));
                    }}
                    className={`p-3 rounded-lg border-2 text-sm font-medium capitalize ${
                      portfolio?.theme === theme
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {theme}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Share Profile Modal */}
      {publicProfileData?.profile?.publicSlug && (
        <ShareProfileModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          publicSlug={publicProfileData.profile.publicSlug}
          displayName={portfolio?.displayName || 'My'}
        />
      )}
    </div>
  );
}

// Icon components
// ============================================
// SAMPLE CALLS MANAGER
// ============================================

/**
 * Featured convention: orderIndex 1-3 + isPublic = featured.
 * orderIndex 0 or >3 = regular. No schema change needed.
 */
const MAX_FEATURED = 3;
function isFeatured(h: PortfolioHighlight): boolean {
  return h.isPublic && h.orderIndex >= 1 && h.orderIndex <= MAX_FEATURED;
}

function SampleCallsManager({ highlights }: { highlights: PortfolioHighlight[] }) {
  const [upsertHighlight] = useUpsertHighlightMutation();
  const [deleteHighlight] = useDeleteHighlightMutation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const publicCount = highlights.filter((h) => h.isPublic).length;
  const featuredCount = highlights.filter(isFeatured).length;

  // Sort: featured first (by orderIndex), then public, then private
  const sorted = [...highlights].sort((a, b) => {
    const aFeat = isFeatured(a) ? 0 : 1;
    const bFeat = isFeatured(b) ? 0 : 1;
    if (aFeat !== bFeat) return aFeat - bFeat;
    if (a.isPublic !== b.isPublic) return a.isPublic ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const handleUpdate = async (highlight: PortfolioHighlight, updates: Partial<{ title: string; isPublic: boolean; orderIndex: number }>) => {
    try {
      await upsertHighlight({
        id: highlight.id,
        sourceType: highlight.sourceType,
        sourceId: highlight.sourceId,
        title: updates.title ?? highlight.title,
        isPublic: updates.isPublic ?? highlight.isPublic,
        orderIndex: updates.orderIndex ?? highlight.orderIndex,
      }).unwrap();
      return true;
    } catch {
      toast.error('Could not update. Please try again.');
      return false;
    }
  };

  const handleTogglePublic = async (highlight: PortfolioHighlight) => {
    const newPublic = !highlight.isPublic;
    // If making private and was featured, unfeature too
    const newOrder = !newPublic && isFeatured(highlight) ? 0 : highlight.orderIndex;
    if (await handleUpdate(highlight, { isPublic: newPublic, orderIndex: newOrder })) {
      toast.success(newPublic ? 'Now visible on your public profile' : 'Moved to private');
    }
  };

  const handleToggleFeatured = async (highlight: PortfolioHighlight) => {
    if (!highlight.isPublic) {
      toast.error('Make this call public first before featuring it');
      return;
    }
    if (isFeatured(highlight)) {
      // Unfeature: set orderIndex to 0
      if (await handleUpdate(highlight, { orderIndex: 0 })) {
        toast.success('Removed from featured');
      }
    } else {
      if (featuredCount >= MAX_FEATURED) {
        toast.error(`You can feature up to ${MAX_FEATURED} sample calls`);
        return;
      }
      // Feature: assign next available orderIndex 1-3
      const usedSlots = new Set(highlights.filter(isFeatured).map((h) => h.orderIndex));
      let slot = 1;
      while (usedSlots.has(slot) && slot <= MAX_FEATURED) slot++;
      if (await handleUpdate(highlight, { orderIndex: slot })) {
        toast.success('Featured on your public profile');
      }
    }
  };

  const handleStartRename = (highlight: PortfolioHighlight) => {
    setEditingId(highlight.id);
    setEditTitle(highlight.title);
  };

  const handleSaveRename = async (highlight: PortfolioHighlight) => {
    if (!editTitle.trim() || editTitle.trim() === highlight.title) {
      setEditingId(null);
      return;
    }
    if (await handleUpdate(highlight, { title: editTitle.trim() })) {
      setEditingId(null);
      toast.success('Renamed');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteHighlight(id).unwrap();
      setConfirmDeleteId(null);
      toast.success('Sample call removed');
    } catch {
      toast.error('Could not remove. Please try again.');
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  };

  return (
    <>
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Sample Calls</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {highlights.length > 0
                ? `${featuredCount} featured · ${publicCount} public · ${highlights.length} total`
                : 'Showcase how you perform in real conversations'}
            </p>
          </div>
          <Link
            to="/practice"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Record New
          </Link>
        </div>
        <div className="card-body">
          {sorted.length > 0 ? (
            <div className="space-y-2">
              {sorted.map((highlight) => {
                const featured = isFeatured(highlight);
                return (
                  <div
                    key={highlight.id}
                    className={`rounded-lg border p-3 transition-colors ${
                      featured
                        ? 'border-primary-300 bg-primary-50/50 ring-1 ring-primary-200'
                        : highlight.isPublic
                        ? 'border-green-200 bg-green-50/50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        featured ? 'bg-primary-100' : highlight.isPublic ? 'bg-green-100' : 'bg-gray-200'
                      }`}>
                        <MicrophoneIcon className={`w-5 h-5 ${
                          featured ? 'text-primary-600' : highlight.isPublic ? 'text-green-600' : 'text-gray-500'
                        }`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {editingId === highlight.id ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveRename(highlight);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              className="text-sm font-medium text-gray-900 border border-primary-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 flex-1"
                              autoFocus
                            />
                            <button onClick={() => handleSaveRename(highlight)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Save</button>
                            <button onClick={() => setEditingId(null)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                          </div>
                        ) : (
                          <h4 className="font-medium text-gray-900 truncate">{highlight.title}</h4>
                        )}
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                          {highlight.durationSeconds && <span>{formatDuration(highlight.durationSeconds)}</span>}
                          <span className="capitalize">{highlight.sourceType.replace(/_/g, ' ')}</span>
                          <span>{new Date(highlight.createdAt).toLocaleDateString()}</span>
                        </div>
                        {highlight.result && <ResultProvenance result={highlight.result} testId="result-item-provenance" />}
                      </div>

                      {/* Status badges */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {featured && (
                          <span className="px-2 py-1 text-xs rounded-full bg-primary-100 text-primary-700 font-medium">
                            Featured
                          </span>
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          highlight.isPublic ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {highlight.isPublic ? 'Public' : 'Private'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    {editingId !== highlight.id && (
                      <div className="flex items-center gap-3 mt-2 pl-13">
                        {highlight.isPublic && (
                          <>
                            <button
                              onClick={() => handleToggleFeatured(highlight)}
                              className={`text-xs transition-colors ${
                                featured
                                  ? 'text-primary-600 hover:text-primary-800 font-medium'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              {featured ? 'Unfeature' : 'Feature'}
                            </button>
                            <span className="text-gray-300">·</span>
                          </>
                        )}
                        <button onClick={() => handleTogglePublic(highlight)} className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
                          {highlight.isPublic ? 'Make Private' : 'Make Public'}
                        </button>
                        <span className="text-gray-300">·</span>
                        <button onClick={() => handleStartRename(highlight)} className="text-xs text-gray-500 hover:text-gray-700 transition-colors">Rename</button>
                        <span className="text-gray-300">·</span>
                        {confirmDeleteId === highlight.id ? (
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-red-600">Remove?</span>
                            <button onClick={() => handleDelete(highlight.id)} className="text-xs text-red-600 hover:text-red-800 font-medium">Yes</button>
                            <button onClick={() => setConfirmDeleteId(null)} className="text-xs text-gray-500 hover:text-gray-700">No</button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmDeleteId(highlight.id)} className="text-xs text-red-500 hover:text-red-700 transition-colors">Remove</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <MicrophoneIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">No sample calls yet</h3>
              <p className="text-sm text-gray-500 max-w-xs mx-auto mb-4">
                Add examples of how you perform in real conversations.
                Practice a call, then save your best sessions here.
              </p>
              <Link to="/practice" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium">
                Start a Practice Call
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Nudges */}
      {highlights.length > 0 && publicCount === 0 && (
        <div className="card bg-amber-50 border-amber-200">
          <div className="card-body flex items-start gap-3">
            <span className="text-lg">💡</span>
            <div>
              <p className="text-sm font-medium text-amber-900">None of your sample calls are public yet</p>
              <p className="text-xs text-amber-800 mt-0.5">Mark your strongest examples as Public so they appear on your profile.</p>
            </div>
          </div>
        </div>
      )}
      {publicCount > 0 && featuredCount === 0 && (
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-body flex items-start gap-3">
            <span className="text-lg">⭐</span>
            <div>
              <p className="text-sm font-medium text-blue-900">Feature your best calls</p>
              <p className="text-xs text-blue-800 mt-0.5">Featured sample calls appear prominently at the top of your public profile. Select up to {MAX_FEATURED}.</p>
            </div>
          </div>
        </div>
      )}
      {highlights.length === 0 && (
        <div className="card bg-gradient-to-r from-primary-50 to-blue-50 border-primary-200">
          <div className="card-body">
            <h3 className="font-semibold text-gray-900 mb-2">Why add sample calls?</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2"><span className="text-primary-500 mt-0.5">1.</span><span>Show recruiters and employers how you actually perform</span></li>
              <li className="flex items-start gap-2"><span className="text-primary-500 mt-0.5">2.</span><span>Featured examples appear prominently on your public profile</span></li>
              <li className="flex items-start gap-2"><span className="text-primary-500 mt-0.5">3.</span><span>Choose your strongest examples to improve discoverability</span></li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================
// ICONS
// ============================================

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );
}

function MicrophoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}
